import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

export default function Contact() {
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you for writing to us. We will reply within one working day.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 pb-24 pt-40 lg:px-8 lg:pb-32" data-testid="contact-page">
      <div className="grid gap-20 lg:grid-cols-2">
        <Reveal>
          <p className="overline">Contact</p>
          <h1 className="mt-4 font-serif text-4xl font-light leading-tight text-[#262019] sm:text-5xl lg:text-6xl">
            We are <span className="italic text-[#A58343]">listening</span>
          </h1>
          <p className="mt-8 max-w-md text-base leading-relaxed text-[#7C7163]">
            Questions about a piece, an order, certification or a bespoke commission —
            write to us and a real person from our team will reply.
          </p>

          <div className="mt-12 space-y-8 border-t border-[#A58343]/20 pt-12">
            <div className="flex items-center gap-5">
              <Phone size={20} strokeWidth={1.25} className="shrink-0 text-[#A58343]" />
              <div>
                <p className="overline !text-[10px]">Phone / WhatsApp</p>
                <a href="tel:+8801713044029" data-testid="contact-phone" className="mt-1 block text-sm text-[#262019] transition-colors duration-300 hover:text-[#A58343]">
                  +880 1713 044029
                </a>
                <a
                  href="https://wa.me/8801713044029?text=Hello%20Merakee%2C%20I%20would%20like%20to%20enquire%20about%20your%20jewellery."
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-whatsapp"
                  className="mt-1 inline-block text-xs tracking-[0.2em] text-[#A58343] underline-offset-4 transition-colors duration-300 hover:text-[#8C6D33] hover:underline"
                >
                  CHAT ON WHATSAPP
                </a>
              </div>
            </div>
            <div className="flex items-center gap-5">
              <Clock size={20} strokeWidth={1.25} className="shrink-0 text-[#A58343]" />
              <div>
                <p className="overline !text-[10px]">Consultations</p>
                <p className="mt-1 text-sm text-[#262019]">By appointment · Mon – Sat · 10 AM – 8 PM IST</p>
              </div>
            </div>
          </div>

          <p className="mt-12 text-sm leading-relaxed text-[#7C7163]">
            Planning a purchase? The fastest way is a{" "}
            <Link to="/consultation" data-testid="contact-consult-link" className="text-[#A58343] underline-offset-4 transition-colors duration-300 hover:text-[#8C6D33] hover:underline">
              private consultation
            </Link>
            .
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="grain border border-[#A58343]/25 bg-[#FFFFFF] p-10 lg:p-14" data-testid="contact-form">
            <div className="space-y-8">
              <div>
                <label htmlFor="ct-name" className="overline !text-[10px]">Full Name</label>
                <input id="ct-name" required value={form.name} onChange={set("name")} placeholder="Your name" className="field mt-2" data-testid="contact-name-input" />
              </div>
              <div className="grid gap-8 sm:grid-cols-2">
                <div>
                  <label htmlFor="ct-email" className="overline !text-[10px]">Email</label>
                  <input id="ct-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="field mt-2" data-testid="contact-email-input" />
                </div>
                <div>
                  <label htmlFor="ct-phone" className="overline !text-[10px]">Phone (optional)</label>
                  <input id="ct-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 …" className="field mt-2" data-testid="contact-phone-input" />
                </div>
              </div>
              <div>
                <label htmlFor="ct-message" className="overline !text-[10px]">Message</label>
                <textarea id="ct-message" required rows={5} value={form.message} onChange={set("message")} placeholder="How can we help?" className="field mt-2 resize-none" data-testid="contact-message-input" />
              </div>
              <button type="submit" disabled={sending} className="btn-gold-solid w-full disabled:opacity-50" data-testid="contact-submit-button">
                {sending ? "Sending…" : "Send Message"}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
